import { Box, Typography, Autocomplete, TextField } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { headingH6, textSM, textXS } from "../../../shared/constants";
import colors from "../../../shared/constants/color";
import { useWiserTheme } from "../../../hooks/useWiserTheme";
import { getInterviewResultTagColors } from "../../../shared/theme/wiserThemeHelpers";
import { Button } from "../../../components";
import { VideoLightIcon } from "../../../shared/images/icons";
import "../../../shared/utils/textUtils";
import { InterviewStatusTag } from "./shared/InterviewStatusTag";
import {
  InterviewResultParameterGuids,
  isNegativeInterviewResult,
} from "../../../constants/interviewResultParameterGuids";
import {
  candidateHeaderRootSx,
  candidateHeaderLeftSx,
  candidateHeaderFieldsSx,
  headerAutocompleteInputSx,
  getHeaderAutocompleteSx,
  getManuelScoreColors,
} from "../style";

export interface InterviewResultOption {
  /** InterviewResultParameter.ID (Guid). Upstream'de int'ti. */
  value: string;
  label: string;
}

export type NegativeReasonOption = InterviewResultOption;

interface IProps {
  fullName: string;
  techGroups: string[];
  subStatus: string;
  interviewDate: string;
  kvkkStatus: string;
  interviewResultId: string | null;
  interviewResultOptions: InterviewResultOption[];
  onInterviewResultChange?: (interviewResultId: string | null) => void;
  /** upstream 5a8f50f: bu alt durumda mülakat sonucu boşaltılamaz. */
  interviewResultRequired?: boolean;
  negativeReasonId: string | null;
  negativeReasonOptions: NegativeReasonOption[];
  /** upstream 5a8f50f: alan yalnızca haritada karşılığı olan alt durumlarda. */
  showNegativeReason?: boolean;
  onNegativeReasonChange?: (
    negativeReasonId: string | null,
    options?: { silent?: boolean },
  ) => void;
  /** upstream 942593f: 0-100 manuel skor, "Olumsuz" seçilince zorunlu. */
  manuelScore: number | null;
  onManuelScoreChange?: (manuelScore: number | null) => void;
  onStartVideoCall: () => void;
  recordingPopupOpen?: boolean;
}

export const CandidateHeader = ({
  fullName,
  techGroups,
  subStatus,
  interviewDate,
  kvkkStatus,
  interviewResultId,
  interviewResultOptions,
  onInterviewResultChange,
  interviewResultRequired = false,
  negativeReasonId,
  negativeReasonOptions,
  showNegativeReason = true,
  onNegativeReasonChange,
  manuelScore,
  onManuelScoreChange,
  onStartVideoCall,
  recordingPopupOpen,
}: IProps) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const [localInterviewResultId, setLocalInterviewResultId] = useState<
    string | null
  >(interviewResultId);
  const [localNegativeReasonId, setLocalNegativeReasonId] = useState<
    string | null
  >(negativeReasonId);
  const [localManuelScore, setLocalManuelScore] = useState(
    manuelScore != null ? String(manuelScore) : "",
  );
  const [manuelScoreTouched, setManuelScoreTouched] = useState(false);

  useEffect(() => {
    setLocalInterviewResultId(interviewResultId);
  }, [interviewResultId]);

  useEffect(() => {
    setLocalNegativeReasonId(negativeReasonId);
  }, [negativeReasonId]);

  useEffect(() => {
    setLocalManuelScore(manuelScore != null ? String(manuelScore) : "");
  }, [manuelScore]);

  const selectedOption = interviewResultOptions.find(
    (option) => option.value === localInterviewResultId,
  );
  const inputSize = selectedOption ? selectedOption.label.length + 4 : 10;

  const selectedNegativeOption = negativeReasonOptions.find(
    (option) => option.value === localNegativeReasonId,
  );
  // Ust sinir 18: maxWidth=170 ile eslenik. Sinir olmazsa uzun olumsuz-neden
  // etiketleri size'i buyutuyor, maxWidth de onu kirpiyordu (upstream 942593f).
  const negativeInputSize = selectedNegativeOption
    ? Math.min(selectedNegativeOption.label.length + 2, 18)
    : 16;

  const isOlumsuz = useMemo(
    () => isNegativeInterviewResult(localInterviewResultId, selectedOption?.label),
    [localInterviewResultId, selectedOption],
  );

  // Anlamsal renkler (olumlu yesil / olumsuz-iptal kirmizi / diger mor)
  // paylasilan yardimcida da korunuyor; yardimci ek olarak koyu tema
  // tonlarini tasiyor. Bkz. asagidaki "BILINCLI SAPMA" notu.
  const interviewResultColors = getInterviewResultTagColors(
    localInterviewResultId,
    darkModeActive,
  );

  const negativeReasonColor = {
    bg: colors.red.R50,
    text: colors.red.R500,
  };

  const negativeReasonRequired = isOlumsuz && showNegativeReason;
  const manuelScoreRequired = isOlumsuz;
  const interviewResultError =
    interviewResultRequired && localInterviewResultId == null;
  const negativeReasonError =
    negativeReasonRequired && localNegativeReasonId == null;

  const parsedManuelScore = Number(localManuelScore.trim());
  const isManuelScoreValid =
    localManuelScore.trim() !== "" &&
    !Number.isNaN(parsedManuelScore) &&
    parsedManuelScore >= 0 &&
    parsedManuelScore <= 100;
  const manuelScoreError =
    manuelScoreRequired &&
    (manuelScoreTouched || localManuelScore.trim() === "")
      ? !isManuelScoreValid
      : false;

  /*
   * Manuel skor alanı — upstream 942593f `getManuelScoreColors`.
   *
   * BİLİNÇLİ SAPMA: upstream başlıktaki ÜÇ alanın hepsini aynı mavi tona
   * çeviriyor. Saas'ta mülakat sonucu rengi sonuca göre yeşil/kırmızı/mor
   * (getInterviewResultColor) ve bu bilgi taşıyor — korunuyor. Mavi yalnız
   * manuel skor alanına uygulanıyor.
   *
   * Sapma yalnızca KAPSAMDA: renk değerleri gece modu farkındalığıyla birlikte
   * upstream'in yardımcısından geliyor (daha önce burada aydınlık tema değerleri
   * sabitlenmişti ve yardımcı ölü koddu).
   */
  const manuelScoreColor = getManuelScoreColors(darkModeActive, tokens);

  const manuelScoreFieldSx = (
    bg: string,
    text: string,
    hasError?: boolean,
  ) => ({
    display: "inline-flex",
    width: "52px",
    minWidth: "52px",
    "& .MuiOutlinedInput-root": {
      width: "52px",
      minWidth: "52px",
      backgroundColor: bg,
      borderRadius: "3px",
      height: "28px",
      padding: "0 !important",
      "& .MuiOutlinedInput-notchedOutline": {
        border: hasError ? `1px solid ${colors.red.R500}` : "none",
      },
      "& .MuiOutlinedInput-input": {
        ...textXS(500, text),
        padding: "4px 6px !important",
        width: "100% !important",
        minWidth: "0 !important",
        textAlign: "center",
      },
    },
  });

  const handleInterviewResultChange = (
    newValue: InterviewResultOption | null,
  ) => {
    // upstream 5a8f50f: zorunlu alt durumda temizleme yok sayılıyor.
    if (interviewResultRequired && newValue == null) {
      return;
    }

    const newInterviewResultId = newValue?.value ?? null;
    setLocalInterviewResultId(newInterviewResultId);

    // Sonuc artik olumsuz degilse secili olumsuz nedeni sessizce temizle —
    // kullaniciya iki ayri toast gostermemek icin silent.
    const nextIsOlumsuz = isNegativeInterviewResult(
      newInterviewResultId,
      newValue?.label,
    );

    if (!nextIsOlumsuz && localNegativeReasonId != null) {
      setLocalNegativeReasonId(null);
      onNegativeReasonChange?.(null, { silent: true });
    }

    if (nextIsOlumsuz) {
      setManuelScoreTouched(true);
    }

    if (onInterviewResultChange) {
      onInterviewResultChange(newInterviewResultId);
    }
  };

  const handleNegativeReasonChange = (
    newValue: NegativeReasonOption | null,
  ) => {
    if (negativeReasonRequired && newValue == null) {
      return;
    }

    const newNegativeReasonId = newValue?.value ?? null;
    setLocalNegativeReasonId(newNegativeReasonId);
    if (onNegativeReasonChange) {
      onNegativeReasonChange(newNegativeReasonId);
    }
  };

  const handleManuelScoreChange = (value: string) => {
    if (value === "" || /^\d{1,3}$/.test(value)) {
      setLocalManuelScore(value);
      setManuelScoreTouched(true);
    }
  };

  const commitManuelScore = (rawValue?: string) => {
    if (!manuelScoreRequired) return;
    setManuelScoreTouched(true);
    const raw = (rawValue ?? localManuelScore).trim();
    const num = Number(raw);
    const valid = raw !== "" && !Number.isNaN(num) && num >= 0 && num <= 100;
    if (!valid) return;
    if (num === manuelScore) return;
    onManuelScoreChange?.(num);
  };

  return (
    <Box sx={candidateHeaderRootSx}>
      <Box sx={candidateHeaderLeftSx}>
        <Typography
          sx={headingH6(
            600,
            darkModeActive ? tokens.textPrimary : colors.neutral.N700,
          )}
        >
          {fullName}
        </Typography>
        <InterviewStatusTag status={subStatus} interviewDate={interviewDate} />
        <Typography
          sx={textSM(
            400,
            darkModeActive ? tokens.textPrimary : colors.neutral.N300,
          )}
        >
          {techGroups.join(" - ") + " • " + kvkkStatus}
        </Typography>
        <Box sx={candidateHeaderFieldsSx}>
          <Autocomplete
            key={`interview-result-${localInterviewResultId}-${inputSize}`}
            value={
              interviewResultOptions.find(
                (option) => option.value === localInterviewResultId,
              ) || null
            }
            onChange={(_, newValue) => handleInterviewResultChange(newValue)}
            options={interviewResultOptions}
            getOptionLabel={(option) => option.label}
            disableClearable={
              interviewResultRequired && localInterviewResultId != null
            }
            size="small"
            sx={getHeaderAutocompleteSx(
              interviewResultColors.bg,
              interviewResultColors.text,
              interviewResultError,
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Mülakat sonucu"
                error={interviewResultError}
                title={
                  interviewResultError
                    ? "Lütfen mülakat sonucu seçiniz"
                    : undefined
                }
                inputProps={{
                  ...params.inputProps,
                  size: inputSize,
                }}
                sx={headerAutocompleteInputSx}
              />
            )}
          />
          {isOlumsuz && showNegativeReason && (
            <Autocomplete
              key={`negative-reason-${localNegativeReasonId}-${negativeInputSize}`}
              value={
                negativeReasonOptions.find(
                  (option) => option.value === localNegativeReasonId,
                ) || null
              }
              onChange={(_, newValue) => handleNegativeReasonChange(newValue)}
              options={negativeReasonOptions}
              getOptionLabel={(option) => option.label}
              disableClearable={
                negativeReasonRequired && localNegativeReasonId != null
              }
              size="small"
              sx={getHeaderAutocompleteSx(
                negativeReasonColor.bg,
                negativeReasonColor.text,
                negativeReasonError,
                170,
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Olumsuz nedeni"
                  error={negativeReasonError}
                  title={
                    negativeReasonError
                      ? "Lütfen olumsuz nedenini seçiniz"
                      : undefined
                  }
                  inputProps={{
                    ...params.inputProps,
                    size: negativeInputSize,
                  }}
                  sx={headerAutocompleteInputSx}
                />
              )}
            />
          )}
          {isOlumsuz && (
            <TextField
              size="small"
              variant="outlined"
              placeholder="Manuel skor"
              value={localManuelScore}
              onChange={(e) => handleManuelScoreChange(e.target.value)}
              onBlur={() => commitManuelScore()}
              onKeyDown={(e) => {
                if (["-", "+", "e", "E", ".", ","].includes(e.key)) {
                  e.preventDefault();
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitManuelScore();
                }
              }}
              error={manuelScoreError}
              title={
                manuelScoreError
                  ? "Lütfen 0-100 arası bir değer giriniz"
                  : "Manuel skor"
              }
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: 0,
                max: 100,
                size: 3,
              }}
              sx={manuelScoreFieldSx(
                manuelScoreColor.bg,
                manuelScoreColor.text,
                manuelScoreError,
              )}
            />
          )}
        </Box>
      </Box>
      {!recordingPopupOpen && (
        <Button
          hasIcon
          iconSize="md"
          iconSpace="8px"
          variant="contained"
          size="md"
          color={colors.deepBlue}
          fontWeight="medium"
          onClick={onStartVideoCall}
        >
          <img src={VideoLightIcon} />
          Görüşme Başlat
        </Button>
      )}
    </Box>
  );
};
